import { Auth as SupabaseAuth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "@/i18n/useTranslation";
import { Language } from "@/i18n/translations";
import { AuthError, AuthApiError } from "@supabase/supabase-js";
import { AppRole, getPostAuthRoute } from "@/utils/routePermissions";
import { useToast } from "@/components/ui/use-toast";

export default function Auth() {
  const navigate = useNavigate();
  const [error, setError] = useState<string>("");
  const { t, language, setLanguage } = useTranslation();
  const { toast } = useToast();

  const getErrorMessage = (error: AuthError) => {
    if (error instanceof AuthApiError) {
      switch (error.code) {
        case 'invalid_credentials':
          return t('auth.invalidCredentials') || 'Invalid email or password. Please check your credentials and try again.';
        case 'email_not_confirmed':
          return t('auth.emailNotConfirmed') || 'Please verify your email address before signing in.';
        case 'user_not_found':
          return t('auth.userNotFound') || 'No user found with these credentials.';
        default:
          return error.message;
      }
    }
    return error.message;
  };

  // Redirect user based on role
  const redirectBasedOnRole = async (userId: string) => {
    try {
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);
      
      if (rolesError) throw rolesError;
      
      // Default to user role if no roles found
      const roles = userRoles?.map(r => r.role as AppRole) || [];

      // Redirect based on highest privilege
      navigate(getPostAuthRoute(roles));
    } catch (err) {
      console.error('Error checking user role:', err);
      // Default to dashboard on error
      navigate('/dashboard');
    }
  };

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        setError(getErrorMessage(sessionError));
        return;
      }

      if (session) {
        try {
          // A DB trigger provisions company/role at signup, so the profile
          // normally exists by the time we get here. Retry briefly to absorb
          // any lag, then proceed regardless — the trigger never blocks
          // signup, so a missing profile is a degraded-but-usable state.
          let companyId: number | null = null;
          for (let attempt = 0; attempt < 3; attempt++) {
            const { data: profile, error: profileError } = await supabase
              .from("users")
              .select("company_id")
              .eq("user_id", session.user.id)
              .limit(1)
              .maybeSingle();

            if (profileError) throw profileError;

            if (profile?.company_id) {
              companyId = profile.company_id;
              break;
            }
            await new Promise((resolve) => setTimeout(resolve, 500));
          }

          if (!companyId) {
            toast({
              title: t('auth.setupIncompleteTitle'),
              description: t('auth.setupIncompleteDescription'),
            });
          }

          await redirectBasedOnRole(session.user.id);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'An error occurred while fetching profile');
        }
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        checkSession();
      }
      if (event === 'SIGNED_OUT') {
        setError('');
      }
    });

    checkSession();
    return () => subscription.unsubscribe();
  }, [navigate, language]);

  const handleLanguageChange = (newLanguage: string) => {
    // Fix here: Ensure newLanguage is of type Language
    setLanguage(newLanguage as Language);
    setError(''); // Clear any existing errors when language changes
  };

  return (
    <div className="container max-w-lg mx-auto py-10">
      <h1 className="text-2xl font-bold mb-8 text-center">
        {t('auth.welcome')}
      </h1>
      
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">
          {t('auth.languageSelect')}
        </label>
        <Select 
          value={language} 
          onValueChange={(value) => handleLanguageChange(value)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="en">English</SelectItem>
            <SelectItem value="es">Español</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <div className="border rounded-lg p-6 bg-card">
        <SupabaseAuth
          supabaseClient={supabase}
          appearance={{ 
            theme: ThemeSupa,
            variables: {
              default: {
                colors: {
                  brand: 'rgb(37, 99, 235)',
                  brandAccent: 'rgb(29, 78, 216)'
                }
              }
            }
          }}
          providers={['google']}
          redirectTo={`${window.location.origin}/auth`}
          localization={{
            variables: {
              sign_in: {
                email_label: language === "es" ? "Correo electrónico" : "Email",
                password_label: language === "es" ? "Contraseña" : "Password",
                button_label: language === "es" ? "Iniciar sesión" : "Sign in",
              },
              sign_up: {
                email_label: language === "es" ? "Correo electrónico" : "Email",
                password_label: language === "es" ? "Contraseña" : "Password",
                button_label: language === "es" ? "Registrarse" : "Sign up",
              }
            }
          }}
          view="sign_in"
          additionalData={{
            preferred_language: language,
          }}
        />
      </div>
    </div>
  );
}
